import { el, clear } from '../../core/dom.js';
import { toast } from '../../components/toast.js';
import { empty } from '../../components/empty.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { listModels, setDefaultModel, setModelEnabled } from './actions.js';
import { isAdmin } from './util.js';
import {
  canToggleModel,
  findModel,
  modelDetailChips,
  modelDetailHref,
  modelDetailStats,
  modelsListHref,
} from './model-detail-model.js';

/**
 * AI model detail screen (plan 4 R11). Uses GET /models fields only.
 * @param {{
 *   modelId: string,
 *   productId: string,
 *   me: object,
 * }} opts
 */
export function renderModelDetail(opts) {
  const admin = isAdmin(opts.me);
  const root = el('div', { class: 'set md' });
  const host = el('div');
  root.append(host);

  const ui = {
    dead: false,
    loading: true,
    fatal: '',
    models: [],
    defaultModelId: null,
    busy: false,
  };

  root.addEventListener('bonnie:unmount', () => { ui.dead = true; });

  function paint() {
    if (ui.dead) return;
    clear(host);
    if (ui.loading) {
      host.append(skeleton(5));
      return;
    }
    if (ui.fatal) {
      host.append(errorPanel({
        title: 'Model unavailable',
        message: ui.fatal,
        action: button('Back to AI models', {
          variant: 'primary',
          href: modelsListHref(opts.productId),
        }),
      }));
      return;
    }
    const model = findModel(ui.models, opts.modelId);
    if (!model) {
      host.append(empty({
        title: 'Model not found',
        body: 'That model is not in this workspace\'s catalog.',
        action: button('Back to AI models', {
          variant: 'primary',
          href: modelsListHref(opts.productId),
        }),
      }));
      return;
    }
    host.append(detailView(model, ui, { admin, productId: opts.productId, handlers: {
      onToggle: (enabled) => toggle(model, enabled),
      onDefault: () => saveDefault(model.id),
    } }));
  }

  async function toggle(model, enabled) {
    if (!admin || ui.busy) return;
    const gate = canToggleModel(model, enabled);
    if (!gate.ok) {
      toast(gate.error, 'error');
      return;
    }
    ui.busy = true;
    paint();
    const result = await setModelEnabled(model.id, enabled);
    if (ui.dead) return;
    ui.busy = false;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not update model', 'error');
      await load();
      return;
    }
    toast(`${model.label || model.id} ${enabled ? 'enabled' : 'disabled'}`);
    await load();
  }

  async function saveDefault(modelId) {
    if (!admin || ui.busy) return;
    ui.busy = true;
    paint();
    const result = await setDefaultModel(modelId);
    if (ui.dead) return;
    ui.busy = false;
    if (!result || result.error) {
      toast((result && result.error) || 'Could not set default model', 'error');
      await load();
      return;
    }
    ui.defaultModelId = result.default_model_id || null;
    toast('Default chat model updated');
    paint();
  }

  async function load() {
    ui.loading = true;
    ui.fatal = '';
    paint();
    const data = await listModels();
    if (ui.dead) return;
    ui.loading = false;
    if (!data || data.error) {
      ui.fatal = (data && data.error) || 'Could not load models';
      ui.models = [];
      ui.defaultModelId = null;
    } else {
      ui.fatal = '';
      ui.models = data.models || [];
      ui.defaultModelId = data.default_model_id || null;
    }
    paint();
  }

  load();
  return root;
}

function detailView(model, ui, ctx) {
  const chips = modelDetailChips(model, { defaultModelId: ui.defaultModelId });
  const stats = modelDetailStats(model, { defaultModelId: ui.defaultModelId });
  const on = !!model.enabled;
  return el('div', { class: 'set-stack' },
    el('a', { class: 'md-back', href: modelsListHref(ctx.productId) }, '← AI models'),
    el('header', { class: 'set-head' },
      el('div', { class: 'set-head-copy' },
        el('h1', { class: 'set-title' },
          model.label || model.id,
          ' - ',
          el('em', {}, 'what this model does.')),
        el('p', {}, model.whenToUse || 'Available in every chat\'s model picker when enabled.')),
      ctx.admin
        ? el('label', { class: 'set-switch', title: on ? 'On' : 'Off' },
          el('input', {
            type: 'checkbox',
            checked: on ? true : null,
            disabled: ui.busy ? true : null,
            onChange: (event) => ctx.handlers.onToggle(!!event.target.checked),
          }),
          el('span', { class: 'set-switch-ui', 'aria-hidden': 'true' }))
        : el('span', { class: 'set-chip' }, on ? 'On' : 'Off')),
    el('div', { class: 'md-chips' }, chips.map((chip) => el('span', { class: 'set-chip' }, chip))),
    el('div', { class: 'set-model-stats md-stats' },
      stats.map((stat) => el('div', { class: 'set-stat' },
        el('span', { class: 'lbl' }, stat.label),
        el('span', {}, stat.value)))),
    el('div', { class: 'set-panel' },
      el('div', { class: 'set-panel-head' },
        el('div', { class: 'set-panel-title' }, el('span', {}, 'About'))),
      el('div', { class: 'set-byok-body' },
        el('p', {}, model.whenToUse || 'No guidance text for this model yet.'),
        el('p', { class: 'set-hint' },
          'Model choice never changes what a query can see. Plan limit reads "- (beta)" until free and paid plans ship.'))),
    ctx.admin
      ? el('div', { class: 'md-actions' },
        button(
          ui.defaultModelId === model.id ? 'Already the org default' : 'Make org default',
          {
            variant: 'primary',
            disabled: ui.busy || ui.defaultModelId === model.id,
            onClick: ctx.handlers.onDefault,
          },
        ),
        button('Manage keys', {
          href: modelsListHref(ctx.productId),
        }))
      : null);
}

/** Re-export for settings cards / tests. */
export { modelDetailHref };
